import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DropdownButton, SelectionList, } from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import remove from 'lodash/remove';
import { Fragment, memo, useCallback, useMemo, useRef, useState, } from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analytics } from 'app/utils/segment';
import { isOptionalOperator } from '../../utils/filters/isOptionalOperator';
import { AnalyticsFilterItem } from './components/item/analyticsFilterItem';
import { AnalyticsFilterLayout } from './components/layout/analyticsFilterLayout';
import { AnalyticsFilterOptions } from './components/options/analyticsFilterOptions';
import { AnalyticsFilterSearch } from './components/search/analyticsFilterSearch';
import { AnalyticsFilterSelectAll } from './components/selectAll/analyticsFilterSelectAll';
const AnalyticsFilter = (_a) => {
    var _b;
    var { children, field, availableValues, selectedValues = [], pinnedValues = [], disabledValues = [], availableOperators = [], defaultOperator = (_b = availableOperators === null || availableOperators === void 0 ? void 0 : availableOperators[0]) !== null && _b !== void 0 ? _b : 'is_one', selectedOperator = defaultOperator, isMultiple = true, isSelectionDisabled = false, hasDeterminateSelection = isMultiple, buttonProps = {}, customHeaderElement, onChange, onToggle, } = _a;
    const { dashboard } = useAnalyticsState();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);
    const searchRef = useRef(null);
    const listWrapperRef = useRef(null);
    /**
     * The filter values visible to the user.
     *
     * These are first filtered according to the search criteria if any, and
     * finally ordered so that the values that should be pinned are shown first.
     */
    const visibleValues = useMemo(() => {
        const fuse = new Fuse(availableValues, {
            keys: ['label', 'value'],
            threshold: 0.2,
            distance: 200,
        });
        // The filtered values. Using spread operator to allow mutating the array below where we need to
        // pull out the pinned values.
        const values = [
            ...(debouncedSearchTerm
                ? fuse.search(debouncedSearchTerm).map(({ item }) => item)
                : availableValues),
        ];
        // Extracts the pinned values from the visible values in order to render the at the top.
        const pinned = remove(values, ({ value, deactivated }) => pinnedValues.some(({ value: pinnedValue }) => pinnedValue === value && !deactivated));
        return [...pinned, ...values];
    }, [availableValues, pinnedValues, debouncedSearchTerm]);
    useUpdateEffect(() => {
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(isOpen);
    }, [isOpen]);
    const handleToggleDropdown = useCallback((newIsOpen) => {
        setIsOpen(newIsOpen);
        if (newIsOpen) {
            analytics.analyticsFilterOpened({
                type: field,
                dashboard: dashboard.title,
            });
            if (listWrapperRef.current) {
                listWrapperRef.current.scrollTop = 0;
            }
        }
    }, [dashboard.title, field]);
    /**
     * Handles changing the filter values.
     *
     * If there is at least one value selected, the filter is either added or
     * updated in the state. Otherwise if no values remain selected, the filter is
     * removed from the state.
     */
    const handleChange = useCallback(({ selectedValues, selectedItem, }) => {
        var _a;
        onChange({
            op: selectedValues.length ? 'upsert' : 'remove',
            filter: {
                field,
                values: selectedValues,
                operator: selectedOperator,
            },
        });
        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        // Close the dropdown automatically when selecting a single value.
        if (!isMultiple) {
            setIsOpen(false);
        }
        if (selectedItem.isSelected) {
            analytics.analyticsFilterValueAdded({
                dashboard: dashboard.title,
                type: field,
                value: selectedItem.value.value,
            });
        }
    }, [dashboard.title, field, isMultiple, onChange, selectedOperator]);
    /**
     * Handles changing all filter values at once.
     *
     * If there is at least one value selected, all are deselected and the filter
     * is removed from the state.
     */
    const handleChangeAll = useCallback(() => {
        const isDeselecting = selectedValues.length > 0;
        onChange({
            op: isDeselecting ? 'remove' : 'upsert',
            filter: {
                field,
                values: isDeselecting
                    ? []
                    : visibleValues.map(({ value }) => ({ value })),
                operator: selectedOperator,
            },
        });
        analytics.analyticsFilterValueAdded({
            dashboard: dashboard.title,
            type: field,
            value: isDeselecting ? 'deselect_all' : 'select_all',
        });
    }, [
        dashboard.title,
        field,
        onChange,
        selectedOperator,
        selectedValues.length,
        visibleValues,
    ]);
    /**
     * Handles changing the filter operator.
     *
     * If filter is restored to its default state, i.e. with the default operator
     * and no values selected, the filter is removed from the state.
     *
     * Otherwise if the filter operator is of the optional type, any previously
     * selected values are cleared and the filter is updated in the state.
     */
    const handleChangeOperator = useCallback((operator) => {
        const isDefaultState = operator === (availableOperators === null || availableOperators === void 0 ? void 0 : availableOperators[0]) && !selectedValues.length;
        onChange({
            op: isDefaultState ? 'remove' : 'upsert',
            filter: {
                field,
                values: isOptionalOperator(operator) ? [] : selectedValues,
                operator,
            },
        });
        analytics.analyticsFilterOperatorUpdated({
            dashboard: dashboard.title,
            type: field,
            operator,
        });
    }, [availableOperators, dashboard.title, field, onChange, selectedValues]);
    /** Handles remove the filter from the state. */
    const handleClear = useCallback(() => {
        onChange({
            op: 'remove',
            filter: {
                field,
                values: [],
                operator: selectedOperator,
            },
        });
        analytics.analyticsFilterCleared({
            dashboard: dashboard.title,
            type: field,
        });
    }, [dashboard.title, field, onChange, selectedOperator]);
    const isEmpty = !visibleValues.length;
    return (_jsx(DropdownButton, Object.assign({ show: isOpen, dropdown: _jsxs(AnalyticsFilterLayout, { children: [_jsx(AnalyticsFilterLayout.Row, { children: _jsx(AnalyticsFilterSearch, { ref: searchRef, value: searchTerm, isOpen: isOpen, isDisabled: isSelectionDisabled, onChange: setSearchTerm }) }), isMultiple && hasDeterminateSelection && !isEmpty && (_jsx(AnalyticsFilterLayout.Row, Object.assign({ noPadding: true, borderBottom: true }, { children: _jsx(AnalyticsFilterSelectAll, { selectedValues: selectedValues, visibleValues: visibleValues, isDisabled: isSelectionDisabled, onChange: handleChangeAll }) }))), customHeaderElement && (_jsx(AnalyticsFilterLayout.Row, Object.assign({ noPadding: true, borderBottom: true }, { children: customHeaderElement }))), _jsx(AnalyticsFilterLayout.Row, Object.assign({ ref: listWrapperRef, noPadding: true, scrollable: true }, { children: _jsxs(SelectionList, Object.assign({ isMultiple: isMultiple, isActive: isOpen, isDisabled: isSelectionDisabled, selectedValues: selectedValues, onChange: handleChange, equalityFn: (a, b) => a.value === b.value, defaultActiveItemIndex: 0 }, { children: [visibleValues.map(({ value, label, color, deactivated }, index, list) => {
                                const isFirstDeactivatedItem = Boolean(deactivated &&
                                    (index === 0 || !list[index - 1].deactivated));
                                return (_jsxs(Fragment, { children: [isFirstDeactivatedItem && (_jsx(SelectionList.Divider, { label: "Deactivated" })), _jsx(AnalyticsFilterItem, { value: { value }, label: label, highlight: debouncedSearchTerm, color: color, isDisabled: disabledValues.some((disabledItem) => disabledItem.value === value) })] }, value));
                            }), isEmpty && _jsx(AnalyticsFilterLayout.Empty, {})] })) })), !isEmpty && availableOperators.length > 0 && (_jsx(AnalyticsFilterLayout.Row, Object.assign({ borderTop: true, slottable: true }, { children: _jsx(AnalyticsFilterOptions, { operators: availableOperators, defaultOperator: defaultOperator, selectedOperator: selectedOperator, isMissingSelectedValues: !selectedValues.length, isDropdownOpen: isOpen, onChangeOperator: handleChangeOperator, onClear: handleClear }) })))] }), onToggle: handleToggleDropdown, buttonProps: buttonProps, "data-analytics-filter": field, fixed: true }, { children: children })));
};
const _AnalyticsFilter = memo(AnalyticsFilter);
export { _AnalyticsFilter as AnalyticsFilter };
